import type { CoreConsentContent } from "@tandemdiabetes/sugarmate-api-react-client"
import type { ReactNode } from "react"
import {
  useMemo,
  Fragment,
} from "react"

import { ConsentLink } from "./ConsentLink"

export function ConsentTextWithLinks(props: CoreConsentContent) {
  const {
    text,
    links,
  } = props

  const elementsArray = useMemo(
    () => {
      // if there are no links return the full text
      if (links === undefined || Object.keys(links).length === 0) {
        return [text]
      }

      const linkRegex = new RegExp(
        /\[(.*?)\]/,
        "g",
      )

      const matches = Array.from(text.matchAll(linkRegex))

      // the start and stop indexes for each link within the larger text string
      const indexSet = matches
        .map((match) => {
          if (match.index !== undefined) {
            return [
              match.index,
              match.index + match[0].length,
            ]
          }
          return []
        })
        .filter((v) => v.length > 0)

      const elements: ReactNode[] = []
      let currentIndex = 0

      indexSet.forEach((currentMatch) => {
        // add a text element from currentIndex to the link's start index
        const textString = text.substring(
          currentIndex,
          currentMatch[0],
        )

        elements.push(
          <Fragment key={`${currentIndex}-${currentMatch[0]}`}>
            {textString}
          </Fragment>,
        )

        // add a link element from from the link's start index to the link's stop index
        const linkText = text
          .substring(
            currentMatch[0],
            currentMatch[1],
          )
          // remove square brackets
          .replace(
            /[[\]]/g,
            "",
          )

        const link = links[linkText]
        if (link) {
          elements.push(
            <ConsentLink
              link={link}
              key={`${currentMatch[0]}-${currentMatch[1]}`}
            >
              {linkText}
            </ConsentLink>,
          )
        }

        // assign the current index to the end position of the link
        currentIndex = currentMatch[1]
      })

      // add text after the last link if there is any
      if (currentIndex < text.length - 1) {
        const textString = text.substring(
          currentIndex,
          text.length,
        )

        elements.push(
          <Fragment key={`${currentIndex}-${text.length}`}>
            {textString}
          </Fragment>,
        )
      }

      return elements
    },
    [
      text,
      links,
    ],
  )

  return (
    <>
      {elementsArray}
    </>
  )
}
